<script setup>
import getUuid from 'widget/helpers/uuid';
import { ref, onMounted, onUnmounted, defineEmits, defineExpose } from 'vue';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.js';
import { format, intervalToDuration } from 'date-fns';
import { convertAudio } from './utils/mp3ConversionUtils';

const props = defineProps({
  audioRecordFormat: {
    type: String,
    required: true,
  },
});

const emit = defineEmits([
  'recorderProgressChanged',
  'finishRecord',
  'pause',
  'play',
  'transcriptionReady', // New emit for transcription
  'liveTranscription', // New emit for live transcription updates
]);

const waveformContainer = ref(null);
const wavesurfer = ref(null);
const record = ref(null);
const isRecording = ref(false);
const isPlaying = ref(false);
const hasRecording = ref(false);
const isTranscribing = ref(false);
const transcriptionText = ref('');

const formatTimeProgress = time => {
  const duration = intervalToDuration({ start: 0, end: time });
  return format(
    new Date(0, 0, 0, 0, duration.minutes, duration.seconds),
    'mm:ss'
  );
};

// Speech recognition setup for live transcription during recording
let recognition = null;
if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
  recognition = new (window.SpeechRecognition ||
    window.webkitSpeechRecognition)();
  recognition.continuous = true;
  recognition.interimResults = true;
  recognition.lang = 'en-US';
}

const initWaveSurfer = () => {
  wavesurfer.value = WaveSurfer.create({
    container: waveformContainer.value,
    waveColor: 'transparent', // Hide waveform
    progressColor: 'transparent', // Hide progress
    height: 0, // Hide container height
    barWidth: 0, // Hide bars
    barGap: 0,
    barRadius: 0,
    plugins: [
      RecordPlugin.create({
        scrollingWaveform: false,
        renderRecordedAudio: false,
      }),
    ],
  });

  wavesurfer.value.on('pause', () => emit('pause'));
  wavesurfer.value.on('play', () => emit('play'));

  record.value = wavesurfer.value.plugins[0];

  wavesurfer.value.on('finish', () => {
    isPlaying.value = false;
  });

  record.value.on('record-end', async blob => {
    const audioUrl = URL.createObjectURL(blob);
    const audioBlob = await convertAudio(blob, props.audioRecordFormat);
    const fileName = `${getUuid()}.mp3`;
    const file = new File([audioBlob], fileName, {
      type: props.audioRecordFormat,
    });
    wavesurfer.value.load(audioUrl);

    // Emit finishRecord with the audio file
    emit('finishRecord', {
      name: file.name,
      type: file.type,
      size: file.size,
      file,
    });

    hasRecording.value = true;
    isRecording.value = false;

    // Transcription is now handled in stopRecording() via live recognition
  });

  record.value.on('record-progress', time => {
    emit('recorderProgressChanged', formatTimeProgress(time));
  });
};

const stopRecording = () => {
  if (isRecording.value) {
    record.value.stopRecording();
    isRecording.value = false;

    // Stop live transcription and emit final result
    if (recognition) {
      try {
        recognition.stop();

        // Emit the final transcription if we have one
        if (transcriptionText.value && transcriptionText.value.trim()) {
          emit('transcriptionReady', transcriptionText.value);
        } else {
          // Fallback if no transcription was captured
          emit(
            'transcriptionReady',
            '[Audio recorded - please type your message below]'
          );
        }
      } catch (error) {
        // Handle recognition stop errors silently
      }
    }
  }
};

const startRecording = () => {
  record.value.startRecording();
  isRecording.value = true;
  transcriptionText.value = ''; // Reset transcription

  // Start live transcription if available
  if (recognition) {
    try {
      recognition.start();

      // Set up live transcription event handlers
      recognition.onresult = event => {
        let finalTranscript = '';
        let interimTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i += 1) {
          const transcript = event.results[i][0].transcript;
          const isFinal = event.results[i].isFinal;

          if (isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }

        // Update transcription in real-time
        transcriptionText.value = finalTranscript || interimTranscript;

        // Emit live transcription updates
        emit('liveTranscription', transcriptionText.value);
      };

      recognition.onerror = () => {
        // Handle transcription errors silently
      };

      recognition.onend = () => {
        // Handle transcription end silently
      };
    } catch (error) {
      // Handle recognition start errors silently
    }
  }
};

// Toggle recording - start if not recording, stop if recording
const toggleRecording = () => {
  if (isRecording.value) {
    stopRecording();
  } else {
    startRecording();
  }
};

// Live transcription is now handled in startRecording() and stopRecording()

const playPause = () => {
  if (hasRecording.value) {
    wavesurfer.value.playPause();
    isPlaying.value = !isPlaying.value;
  }
};

const getTranscription = () => {
  return transcriptionText.value;
};

onMounted(() => {
  initWaveSurfer();
  startRecording();
});

onUnmounted(() => {
  // Stop recognition first to prevent errors
  if (recognition) {
    try {
      recognition.stop();
    } catch (error) {
      // Handle recognition stop errors silently
    }
  }

  // Don't destroy wavesurfer if we're in the middle of transcription
  // This prevents the AbortError
  if (wavesurfer.value && !isTranscribing.value) {
    try {
      wavesurfer.value.destroy();
    } catch (error) {
      // Handle wavesurfer destroy errors silently
    }
  }
});

defineExpose({
  playPause,
  stopRecording,
  toggleRecording,
  record,
  getTranscription,
});
</script>

<template>
  <div class="w-full p-1">
    <!-- Hidden waveform container -->
    <div ref="waveformContainer" class="hidden" />

    <!-- Show live recording button when recording -->
    <div v-if="isRecording" class="recording-indicator">
      <div
        class="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300"
      >
        <div class="flex items-center gap-1">
          <div class="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
          <span class="text-xs font-medium">{{
            $t('CONVERSATION.REPLYBOX.RECORDING')
          }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.recording-indicator {
  animation: slideIn 0.3s ease-out;
  padding: 8px 12px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 6px;
  margin: 4px 0;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
